import { Inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Store } from '@ngrx/store'
import {
  AuthActions,
  AuthRedirectService,
  AuthService,
  AuthStorageService,
  ConfigurationService,
  GlobalMessageService,
  GlobalMessageType,
  OAuthLibWrapperService,
  OccEndpointsService,
  OCC_USER_ID_CURRENT,
  RoutingService,
  StateWithClientAuth,
  UserIdService,
  WindowRef
} from '@spartacus/core'

import { CookieService } from 'ngx-cookie-service'
import { Observable } from 'rxjs'
import { filter, take, tap } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'
import { EbCsagentAuthService } from '../../asm/services/eb-csagent-auth.service'
import { securedPagesConfig } from '../eb-auth.config'
import { EbAuthStatePersistenceService } from './eb-auth-state-persistence.service'

@Injectable({
  providedIn: 'root'
})
export class EbAuthService extends AuthService {
  /**
   * Check params in url and if there is an code/token then try to login with those.
   */
  constructor(
    private readonly cookieService: CookieService,
    protected store: Store<StateWithClientAuth>,
    protected userIdService: UserIdService,
    protected oAuthLibWrapperService: OAuthLibWrapperService,
    protected authStorageService: AuthStorageService,
    protected authRedirectService: AuthRedirectService,
    protected routingService: RoutingService,
    protected occEndpoints: OccEndpointsService,
    protected configurationService: ConfigurationService,
    private readonly winRef: WindowRef,
    protected router: Router,
    @Inject(EbAuthStatePersistenceService)
    protected ebAuthStatePersistenceService: EbAuthStatePersistenceService,
    @Inject('Window') protected window,
    protected globalMessageService: GlobalMessageService
  ) {
    super(
      store,
      userIdService,
      oAuthLibWrapperService,
      authStorageService,
      authRedirectService,
      routingService
    )
  }

  get accessToken() {
    return (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    )
  }

  static handleSSOProcesses(
    store: Store<StateWithClientAuth>,
    globalMessageService: GlobalMessageService,
    winRef: WindowRef,
    ebAuthStatePersistenceService: EbAuthStatePersistenceService,
    router: Router,
    cookieService: CookieService,
    authService: EbAuthService,
    csagentAuthService: EbCsagentAuthService
  ) {
    return () => {
      if (/[?&]sso=401\b/.test(winRef.location.search)) {
        const redirectUrl =
          JSON.parse(winRef.localStorage.getItem(EbLocalStorageKey.AUTH_KEY))?.redirectUrl || '/'
        globalMessageService.add(
          {
            key: 'sso.http401'
          },
          GlobalMessageType.MSG_TYPE_ERROR
        )
        router.navigateByUrl(redirectUrl)
      }
      if (/[?&]login=true\b/.test(winRef.location.search)) {
        authService
          .isUserLoggedIn()
          .pipe(
            take(1),
            filter(isUserLoggedIn => !isUserLoggedIn),
            tap(() => {
              return authService.requestTokenFromSapIdp(winRef.location.pathname)
            })
          )
          .subscribe()
      }
      if (/[?&]logout=true\b/.test(winRef.location.search)) {
        ebAuthStatePersistenceService.internalLogout(true)
        // needed to remove queryParam logout=true from url to prevent loop when user wants to relogin
        router.navigateByUrl('/')
      }
      if (
        cookieService.get('asAgent') &&
        (winRef.localStorage.getItem('emulatedUserToken') || cookieService.get('access_token'))
      ) {
        csagentAuthService.restoreCustomerEmulationSession()
      }
      if (
        cookieService.get('temp_customer_emulation_token') &&
        !cookieService.get('access_token')
      ) {
        cookieService.delete('temp_customer_emulation_token', '/', winRef.location.hostname)
      }
    }
  }

  /**
   * Returns `true` if the user is logged in; and `false` if the user is anonymous.
   */
  isUserLoggedIn(): Observable<boolean> {
    return this.ebAuthStatePersistenceService.isValidTokenAvailable()
  }

  /*
   *
   * change: removed oAuthLoginCheck bcs it´s not valid in our SSO scenario
   *
   */
  async checkOAuthParamsInUrl(): Promise<void> {
    try {
      this.internalLogin()
      this.ebAuthStatePersistenceService.syncAccessTokenFromCookie()
    } catch (e) {}
  }

  private internalLogin() {
    if (this.accessToken) {
      this.userIdService.setUserId(OCC_USER_ID_CURRENT)
      this.store.dispatch(new AuthActions.Login())
      this.ebAuthStatePersistenceService.syncAccessTokenFromCookie()
    }
  }

  requestTokenFromSapIdp(redirectRoute = ''): void {
    const baseUrl = this.occEndpoints.getBaseUrl({
      baseUrl: true,
      prefix: false,
      baseSite: false
    })
    // clean up local storage before login to prevent cart not found error
    this.configurationService.unifiedConfig$.pipe(take(1)).subscribe(config => {
      const { baseSite } = config?.context
      baseSite?.forEach(site => {
        this.winRef.localStorage.removeItem(`spartacus⚿${site}⚿cart`)
      })
    })
    this.window.open(`${baseUrl}/samlsinglesignon/saml/login?RelayState=${redirectRoute}`, '_self')
  }

  isSecuredPage(url: string): boolean {
    let isSecured = false
    securedPagesConfig.forEach(value => {
      if (url?.includes(value)) {
        isSecured = true
      }
    })
    return isSecured
  }

  loginWithSapIDP() {
    if (this.accessToken) {
      this.ebAuthStatePersistenceService.syncAccessTokenFromCookie()
    } else {
      this.requestTokenFromSapIdp(this.winRef.location.pathname)
    }
  }
}
